import { BackLink } from '@mdvip/shared/components/blocks/BackLink';
import { BookmarkButton } from '@mdvip/shared/components/blocks/BookmarkButton';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Button, buttonVariants } from '@mdvip/shared/components/ui/button';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { cn } from '@mdvip/shared/lib/utils';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { RecipeIngredients } from './RecipeIngredients';
import { RecipeMetaStrip } from './RecipeMetaStrip';
import { RecipeNutritionFacts } from './RecipeNutritionFacts';
import { RecipeSteps } from './RecipeSteps';
import { RecipeTags } from './RecipeTags';
import { parseServings, toIngredientGroups, toRecipeTags } from './recipeDetail.mapper';
import { type RecipeDetail, useRecipe } from './useRecipe';
import { useToggleRecipeBookmark } from './useToggleRecipeBookmark';

const RECIPES_HREF = '/resources/nutrition/recipes';

function RecipeDetailSkeleton() {
  const { t } = useTranslation('shared');
  return (
    <div role="status" aria-busy="true" aria-label={t('recipes.detail.loadingLabel')} className="flex flex-col gap-10">
      <div className="flex flex-col gap-3">
        <Skeleton className="h-9 w-2/3" />
        <Skeleton className="h-5 w-full max-w-2xl" />
      </div>
      <div className="grid grid-cols-1 gap-8 @[900px]:grid-cols-[1.5fr_1fr]">
        <Skeleton className="aspect-[16/9] w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
      <div className="grid grid-cols-1 gap-10 @[820px]:grid-cols-2">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
}

/**
 * The recipe detail route (`/resources/nutrition/recipes/:id`). Owns the fetch and everything the
 * content components don't: the header, the print action, the four view states, and getting the
 * member back to the gallery page they came from.
 *
 * `@container` on the root is load-bearing, not decoration: every breakpoint below (and inside
 * `RecipeIngredients` and `RecipeNutritionFacts`) is a container query measuring the page, because
 * those sections sit in a half-width column and can't size themselves off the viewport. Remove it
 * and the layout silently collapses to one column at every width.
 */
export function RecipeDetailPage() {
  const { t } = useTranslation('shared');
  const tError = useErrorTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isPending, isError, error, refetch } = useRecipe(id);

  // react-router stamps the first entry in a session's history stack `'default'`. Any other key
  // means the member navigated here from somewhere in the app, so there is a gallery page (with
  // its own ?page=) to go back to. Deliberately not window.history.length, which counts the whole
  // tab's history and can't tell an in-app trail from an unrelated one.
  const arrivedDirectly = location.key === 'default';
  const backLabel = t('recipes.detail.backToRecipes');
  const backLink = arrivedDirectly ? (
    <BackLink href={RECIPES_HREF} label={backLabel} />
  ) : (
    <BackLink onClick={() => navigate(-1)} label={backLabel} />
  );

  if (isError) {
    const code = extractGqlErrorCode(error);
    const notFound = code === 'NOT_FOUND';

    // The not-found branch deliberately drops the header back link: its Alert action goes to the
    // same place under the same label, and two identical links is a worse recovery path than one.
    return (
      <div className="flex flex-col gap-6">
        {notFound ? null : backLink}
        <Alert variant="destructive">
          <Icon name="alertCircle" />
          <AlertTitle>{notFound ? t('recipes.detail.notFound.title') : tError('title')}</AlertTitle>
          <AlertDescription>
            <p>{notFound ? t('recipes.detail.notFound.message') : tError(code)}</p>
            {notFound ? (
              // A real destination, so it stays a link and only borrows the button's styling.
              // Routing it through Button instead would have Base UI stamp `role="button"` onto
              // the anchor, which is exactly the semantics a recovery link should not have.
              <Link to={RECIPES_HREF} className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'mt-3')}>
                {t('recipes.detail.notFound.backToRecipes')}
              </Link>
            ) : (
              <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
                {t('errors.tryAgain')}
              </Button>
            )}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="@container">
      <div className="flex flex-col gap-10">
        {isPending || !data ? (
          <>
            <div className="flex flex-col pb-5">{backLink}</div>
            <RecipeDetailSkeleton />
          </>
        ) : (
          <RecipeDetailContent recipe={data} backLink={backLink} />
        )}
      </div>
    </div>
  );
}

function RecipeDetailContent({ recipe, backLink }: { recipe: RecipeDetail; backLink: ReactNode }) {
  const { t } = useTranslation('shared');
  const { toggle: toggleBookmark } = useToggleRecipeBookmark();

  const actions = (
    <div className="flex flex-wrap items-center gap-3 print:hidden">
      <BookmarkButton
        saved={recipe.isBookmarked}
        onSaveChange={(next) => toggleBookmark({ entityId: recipe.id, title: recipe.title, next })}
        itemTitle={recipe.title}
      />
      <Button variant="secondary" className="min-h-11 gap-2" onClick={() => window.print()}>
        <Icon name="print" size="sm" />
        {t('recipes.detail.print')}
      </Button>
    </div>
  );

  const decisionColumn = (
    <div className="flex flex-col gap-6">
      {actions}
      <RecipeMetaStrip
        prepTime={recipe.prepTime ?? null}
        totalTime={recipe.totalTime ?? null}
        servings={parseServings(recipe.yield)}
      />
      <RecipeTags tags={toRecipeTags(recipe)} />
    </div>
  );

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col pb-5">{backLink}</div>
        <h1 className="max-w-3xl text-h1 font-semibold text-foreground">{recipe.title}</h1>
        {recipe.description && (
          <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">{recipe.description}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-8 @[900px]:grid-cols-[1.5fr_1fr] @[900px]:items-start">
        {/* The frame is the placeholder. It is the design's own hero slot — bordered, muted, 16:9 —
            so a recipe with no photo holds the same footprint instead of reflowing the decision
            column across the full width. */}
        <div
          className={cn(
            'aspect-[16/9] w-full overflow-hidden rounded-xl border bg-muted',
            // On paper the placeholder is just an empty box taking a third of the sheet. A real
            // photo still prints; only the stand-in is dropped.
            !recipe.imageUrl && 'print:hidden',
          )}
        >
          {recipe.imageUrl && <img src={recipe.imageUrl} alt="" className="size-full object-cover" />}
        </div>
        {decisionColumn}
      </div>

      <div className="grid grid-cols-1 gap-10 @[820px]:grid-cols-2">
        <RecipeIngredients groups={toIngredientGroups(recipe.ingredients)} />
        <RecipeSteps steps={recipe.instructions} />
      </div>

      <RecipeNutritionFacts facts={recipe.nutritionFacts} attribution={recipe.copyright ?? ''} />
    </>
  );
}
